<script setup lang="ts">
import { useHead } from "@unhead/vue";
import { onMounted, ref } from "vue";

import GenericModal from "./components/GenericModal.vue";
import SiteFooter from "./components/layout/SiteFooter.vue";
import SiteHeader from "./components/layout/SiteHeader.vue";
import ToastNotification from "./components/ui/ToastNotification.vue";
import { useDebugStore } from "./store";
import { useAccountStore } from "./store/account";

// Default meta tags
useHead({
  title: "Audit d’accessibilité numérique",
  titleTemplate: "%s - Ara",
  meta: [
    {
      name: "description",
      content:
        "Avec Ara, vous évaluez manuellement les 106 critères du RGAA, générez un rapport d’audit et une déclaration d’accessibilité"
    },
    { name: "og:title", content: "Audit d’accessibilité numérique" },
    {
      name: "og:description",
      content:
        "Avec Ara, vous évaluez manuellement les 106 critères du RGAA, générez un rapport d’audit et une déclaration d’accessibilité"
    },
    { name: "og:url", content: "URL" },
    { name: "og:image", content: "image" },
    { name: "og:type", content: "website" },
    { name: "twitter:card", content: "summary_large_image" }
  ]
});

const debugStore = useDebugStore();
debugStore.initDevModeSwitch();

const accountStore = useAccountStore();
onMounted(() => {
  if (accountStore.authToken) {
    accountStore.refreshToken();
  }
});

// Feedback notice display
const feedbackLocalStorageKey = "ara:hide-feedback-notice";
const showFeedbackNotice = ref(!localStorage.getItem(feedbackLocalStorageKey));

function closeFeedbackNotice() {
  showFeedbackNotice.value = false;
  localStorage.setItem(feedbackLocalStorageKey, "true");

  focusPageHeading();
}

// Focus page heading
function focusPageHeading() {
  // Focus page h1 and set attributes if needed
  const pageHeading: HTMLHeadingElement | null =
    document.querySelector("main h1");

  const pageHeadingIsFocusable =
    pageHeading?.hasAttribute("tabindex") &&
    pageHeading.getAttribute("tabindex") === "-1";

  if (!pageHeadingIsFocusable) {
    pageHeading?.setAttribute("tabindex", "-1");
  }

  pageHeading?.focus();
}
</script>

<template>
  <!-- Page title container, filled on page change -->
  <div
    id="page-title-alert"
    class="fr-sr-only"
    role="alert"
    aria-live="polite"
  />

  <div class="fr-skiplinks">
    <nav class="fr-container" role="navigation" aria-label="Accès rapide">
      <ul class="fr-skiplinks__list">
        <li>
          <a class="fr-link" href="#main">Contenu</a>
        </li>
        <li>
          <a class="fr-link" href="#footer">Pied de page</a>
        </li>
      </ul>
    </nav>
  </div>

  <GenericModal />

  <SiteHeader />

  <main id="main" role="main" tabindex="-1">
    <div v-if="showFeedbackNotice" class="fr-notice fr-notice--info">
      <div class="fr-container">
        <div class="fr-notice__body">
          <p>
            <span class="fr-notice__title fr-text--regular">
              Vos avis nous sont précieux pour améliorer cet outil, n’hésitez
              pas à nous faire part de vos retours depuis ce
              <RouterLink :to="{ name: 'feedback' }" target="_blank">
                court formulaire
                <span class="fr-sr-only">(nouvelle fenêtre)</span></RouterLink>.
            </span>
          </p>
          <button
            title="Masquer le message"
            class="fr-btn--close fr-btn"
            @click="closeFeedbackNotice"
          >
            Masquer le message
          </button>
        </div>
      </div>
    </div>

    <div class="container fr-container fr-mb-12w fr-pt-5w">
      <RouterView />
    </div>
  </main>

  <SiteFooter />

  <ToastNotification />
</template>

<style scoped>
[id="main"]:target {
  scroll-margin: 2rem;
}

.container {
  &:has(.top-link) {
    margin-bottom: 4.5rem !important;
  }

  &:has(.back-link) {
    padding-top: 1.5em !important;
  }
}
</style>
